import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../components/Dialog';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import { Checkbox } from '../../components/Checkbox';
import { Text, Strong } from '../../components/Text';
import Loading from '../../components/Loading';
import showErrorToast from '../../components/Toasts/ShowErrorToast';
import { useAiReportDatasets } from '../../hooks/useAiReportDataset';
import { AiReportColumnMapping, AiReportConversion, AiReportTemplateColumn } from '../../models/AiReportHelper';

interface DatasetApplyPreview {
    referredColumnLabel: string;
    totalRows: number;
    matchedCount: number;
    unmatchedCount: number;
    unmatchedSamples: string[];
    matchedSampleRows: { matchValue: string; values: Record<string, string> }[];
}

interface AppliedResult {
    conversion?: AiReportConversion;
    templateHeaders?: AiReportTemplateColumn[];
    mappingPatch: AiReportColumnMapping;
    selectedColumnIds: string[];
    toastMessage: string;
}

interface DatasetApplyDialogProps {
    open: boolean;
    onClose: () => void;
    conversion: AiReportConversion | null;
    sourceHeaders: string[];
    templateHeaders: AiReportTemplateColumn[];
    mapping: AiReportColumnMapping;
    // Set when opened from a single column's "Fill from a Data Set" choice - skips the checklist
    // step in favor of a single "which data set column goes here" picker, and never creates new
    // columns (the target already exists).
    lockedTargetColumnId?: string | null;
    previewDatasetApply: (conversionId: number, datasetId: number, matchSourceColumn: string, datasetColumnKeys: string[]) => Promise<DatasetApplyPreview & { success: boolean }>;
    addTemplateColumns: (conversionId: number, labels: string[]) => Promise<{ success: boolean; conversion: AiReportConversion; newColumns: AiReportTemplateColumn[] }>;
    onApplied: (result: AppliedResult) => void;
}

type Step = 'dataset' | 'match' | 'columns' | 'confirm';

const DatasetApplyDialog: React.FC<DatasetApplyDialogProps> = ({
    open, onClose, conversion, sourceHeaders, templateHeaders, mapping, lockedTargetColumnId,
    previewDatasetApply, addTemplateColumns, onApplied,
}) => {
    const { datasets, fetchDatasets } = useAiReportDatasets();
    const [step, setStep] = useState<Step>('dataset');
    const [datasetId, setDatasetId] = useState('');
    const [matchColumn, setMatchColumn] = useState('');
    // bulk mode: datasetColumnKey -> '__new__' | an existing templateHeaders column id
    const [targets, setTargets] = useState<Record<string, string>>({});
    // locked mode: which single data set column feeds the locked target column
    const [singleKey, setSingleKey] = useState('');
    const [preview, setPreview] = useState<DatasetApplyPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [applying, setApplying] = useState(false);

    const isLocked = lockedTargetColumnId != null;

    useEffect(() => {
        if (!open) return;
        setStep('dataset');
        setDatasetId('');
        setMatchColumn('');
        setTargets({});
        setSingleKey('');
        setPreview(null);
        if (datasets.length === 0) fetchDatasets().catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const selectedDataset = datasets.find(d => String(d.id) === datasetId) || null;
    const lockedColumn = isLocked ? templateHeaders.find(h => h.id === lockedTargetColumnId) || null : null;
    const lockedColumnLabel = lockedColumn ? (lockedColumn.displayName || lockedColumn.originalName) : '';
    const referredLabel = selectedDataset?.columns.find(c => c.key === selectedDataset.referred_column_key)?.label;
    const effectiveTargets: Record<string, string> = isLocked
        ? (singleKey ? { [singleKey]: lockedTargetColumnId! } : {})
        : targets;

    const handlePickDataset = (id: string) => {
        setDatasetId(id);
        setMatchColumn('');
        setTargets({});
        setSingleKey('');
    };

    const toggleColumnKey = (key: string) => {
        setTargets(prev => {
            const next = { ...prev };
            if (next[key]) delete next[key];
            else next[key] = '__new__';
            return next;
        });
    };

    const setColumnTarget = (key: string, target: string) => setTargets(prev => ({ ...prev, [key]: target }));

    const handlePreview = async () => {
        if (!conversion || !selectedDataset) return;
        const keys = Object.keys(effectiveTargets);
        if (keys.length === 0) {
            showErrorToast(isLocked ? 'Pick which data set column to use' : 'Pick at least one column to bring in');
            return;
        }
        setPreviewLoading(true);
        try {
            const res = await previewDatasetApply(conversion.id, selectedDataset.id, matchColumn, keys);
            setPreview(res);
            setStep('confirm');
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to preview this data set apply');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!conversion || !selectedDataset) return;
        const entries = Object.entries(effectiveTargets);
        if (entries.length === 0) return;

        setApplying(true);
        try {
            const newEntries = entries.filter(([, target]) => target === '__new__');
            const createdColumnByKey: Record<string, AiReportTemplateColumn> = {};
            let updatedConversion: AiReportConversion | undefined;
            let updatedTemplateHeaders: AiReportTemplateColumn[] | undefined;
            if (newEntries.length > 0) {
                const labels = newEntries.map(([key]) => selectedDataset.columns.find(c => c.key === key)?.label || key);
                const res = await addTemplateColumns(conversion.id, labels);
                updatedConversion = res.conversion;
                updatedTemplateHeaders = res.conversion.template_headers || [];
                newEntries.forEach(([key], i) => { createdColumnByKey[key] = res.newColumns[i]; });
            }

            const mappingPatch: AiReportColumnMapping = {};
            const selectedColumnIds: string[] = [];
            entries.forEach(([key, target]) => {
                const col = selectedDataset.columns.find(c => c.key === key);
                const targetColId = target === '__new__' ? createdColumnByKey[key]?.id : target;
                if (!targetColId || !col) return;
                mappingPatch[targetColId] = {
                    sourceColumn: null,
                    customFill: {
                        mode: 'dataset',
                        datasetId: selectedDataset.id,
                        datasetColumnKey: key,
                        matchSourceColumn: matchColumn,
                        datasetName: selectedDataset.name,
                        datasetColumnLabel: col.label,
                    },
                };
                selectedColumnIds.push(targetColId);
            });

            const matchStats = preview ? ` — ${preview.matchedCount}/${preview.totalRows} rows matched` : '';
            const toastMessage = isLocked
                ? `"${lockedColumnLabel}" filled from "${selectedDataset.name}"${matchStats}`
                : `Added ${entries.length} column${entries.length === 1 ? '' : 's'} from "${selectedDataset.name}"${matchStats}`;

            onApplied({ conversion: updatedConversion, templateHeaders: updatedTemplateHeaders, mappingPatch, selectedColumnIds, toastMessage });
            onClose();
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to apply this data set');
        } finally {
            setApplying(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} size="lg">
            <DialogTitle>{isLocked ? `Fill "${lockedColumnLabel}" from a Data Set` : 'Add columns from a Data Set'}</DialogTitle>
            <DialogBody className="space-y-4">
                {step === 'dataset' && (
                    <div>
                        <Text className="mb-2 text-sm font-medium">Which data set do you want to use?</Text>
                        <Select value={datasetId} onChange={(e) => handlePickDataset(e.target.value)}>
                            <option value="">Select a data set&hellip;</option>
                            {datasets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Select>
                        {datasets.length === 0 && (
                            <Text className="mt-2 text-xs text-zinc-500">
                                No data sets yet &mdash; create one from the "Data Sets" button first.
                            </Text>
                        )}
                    </div>
                )}

                {step === 'match' && selectedDataset && (
                    <div>
                        <Text className="mb-2 text-sm font-medium">
                            Which column in your report holds the same value as "{referredLabel}"?
                        </Text>
                        <Select value={matchColumn} onChange={(e) => setMatchColumn(e.target.value)}>
                            <option value="">Select a column&hellip;</option>
                            {sourceHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                        </Select>
                    </div>
                )}

                {step === 'columns' && selectedDataset && (
                    isLocked ? (
                        <div>
                            <Text className="mb-2 text-sm font-medium">
                                Which data set column should fill "{lockedColumnLabel}"?
                            </Text>
                            <Select value={singleKey} onChange={(e) => setSingleKey(e.target.value)}>
                                <option value="">Select a column&hellip;</option>
                                {selectedDataset.columns.filter(c => c.key !== selectedDataset.referred_column_key).map(c => (
                                    <option key={c.key} value={c.key}>{c.label}</option>
                                ))}
                            </Select>
                        </div>
                    ) : (
                        <div>
                            <Text className="mb-2 text-sm font-medium">Which columns do you want to bring in?</Text>
                            <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-zinc-950/10 p-3 dark:border-white/10">
                                {selectedDataset.columns
                                    .filter(c => c.key !== selectedDataset.referred_column_key)
                                    .map(c => {
                                        const target = targets[c.key];
                                        const checked = !!target;
                                        return (
                                            <div key={c.key} className="flex items-center gap-3">
                                                <Checkbox checked={checked} onChange={() => toggleColumnKey(c.key)} />
                                                <Text className="flex-1">{c.label}</Text>
                                                {checked && (
                                                    <Select
                                                        className="max-w-[220px]"
                                                        value={target}
                                                        onChange={(e) => setColumnTarget(c.key, e.target.value)}
                                                    >
                                                        <option value="__new__">+ Add as new column</option>
                                                        {templateHeaders.map(h => (
                                                            <option key={h.id} value={h.id}>{h.displayName || h.originalName}</option>
                                                        ))}
                                                    </Select>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )
                )}

                {step === 'confirm' && preview && selectedDataset && (
                    <div className="space-y-3">
                        <Text>
                            Referred column: <Strong>{preview.referredColumnLabel}</Strong> matched against your column "{matchColumn}".
                        </Text>
                        <Text>
                            <Strong>{preview.matchedCount}</Strong> of {preview.totalRows} rows matched.
                            {preview.unmatchedCount > 0 && ` ${preview.unmatchedCount} row(s) did not match and will be left blank.`}
                        </Text>
                        {preview.unmatchedSamples.length > 0 && (
                            <Text className="text-xs text-zinc-500">
                                Unmatched values (sample): {preview.unmatchedSamples.join(', ')}
                            </Text>
                        )}
                        <div>
                            <Text className="mb-1 text-sm font-medium">Columns that will be written:</Text>
                            <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-950 dark:text-white">
                                {Object.entries(effectiveTargets).map(([key, target]) => {
                                    const col = selectedDataset.columns.find(c => c.key === key);
                                    const existing = target !== '__new__' ? templateHeaders.find(h => h.id === target) : null;
                                    const existingRule = existing ? mapping[existing.id] : null;
                                    const willOverwrite = !!(existingRule && (existingRule.sourceColumn || existingRule.customFill));
                                    return (
                                        <li key={key}>
                                            {col?.label} &rarr; {target === '__new__' ? 'a new column' : `existing column "${existing?.displayName || existing?.originalName}"`}
                                            {willOverwrite && <span className="text-amber-600"> (will overwrite its current rule)</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        {preview.matchedSampleRows.length > 0 && (
                            <div>
                                <Text className="mb-1 text-sm font-medium">Sample values to be inserted:</Text>
                                <div className="max-h-64 overflow-auto rounded-lg border border-zinc-950/10 dark:border-white/10">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="text-zinc-500">
                                                <th className="sticky top-0 bg-white px-2 py-1 text-left dark:bg-zinc-900">{preview.referredColumnLabel}</th>
                                                {Object.keys(effectiveTargets).map(key => (
                                                    <th key={key} className="sticky top-0 bg-white px-2 py-1 text-left dark:bg-zinc-900">
                                                        {selectedDataset.columns.find(c => c.key === key)?.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.matchedSampleRows.map((r, i) => (
                                                <tr key={i} className="border-t border-zinc-950/5 dark:border-white/5">
                                                    <td className="px-2 py-1 text-zinc-950 dark:text-white">{r.matchValue}</td>
                                                    {Object.keys(effectiveTargets).map(key => (
                                                        <td key={key} className="px-2 py-1 text-zinc-950 dark:text-white">{r.values[key]}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogBody>
            <DialogActions>
                {step === 'dataset' && (
                    <>
                        <Button plain onClick={onClose}>Cancel</Button>
                        <Button onClick={() => setStep('match')} disabled={!selectedDataset}>Next</Button>
                    </>
                )}
                {step === 'match' && (
                    <>
                        <Button plain onClick={() => setStep('dataset')}>Back</Button>
                        <Button onClick={() => setStep('columns')} disabled={!matchColumn}>Next</Button>
                    </>
                )}
                {step === 'columns' && (
                    <>
                        <Button plain onClick={() => setStep('match')}>Back</Button>
                        <Button onClick={handlePreview} disabled={previewLoading || Object.keys(effectiveTargets).length === 0}>
                            {previewLoading ? <Loading /> : 'Preview'}
                        </Button>
                    </>
                )}
                {step === 'confirm' && (
                    <>
                        <Button plain onClick={() => setStep('columns')} disabled={applying}>Back</Button>
                        <Button onClick={handleConfirm} disabled={applying}>
                            {applying ? <Loading /> : (isLocked ? 'Confirm & Fill' : 'Confirm & Add')}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DatasetApplyDialog;
